/**
 * 공용 알림/확인 다이얼로그 — 전역에서 alert()/window.confirm() 을 대체.
 *
 * <h3>사용법</h3>
 * <p>⚠ 단순 알림은 이 훅의 notify() 를 쓰지 말고 앱 전역 토스트를 쓴다 —
 * import { notify } from "util/toast". 화면마다 모달/토스트가 갈리는 문제로
 * 2026-07-23 에 알림은 토스트로 통일했고, 이 훅은 확인(yes/no) 전용으로 남겼다.
 * 훅의 notify() 는 하위호환용으로만 남아 있고 현재 호출부는 없다.
 *
 * <pre>{`
 *   const { confirmAsync, NotifyHost } = useNotifyDialog();
 *
 *   // 확인 (Promise<boolean>)
 *   const ok = await confirmAsync({ title: "삭제", message: "정말 삭제할까요?" });
 *   if (!ok) return;
 *
 *   // 컴포넌트 트리에 NotifyHost 마운트
 *   return (<>
 *     ...your UI...
 *     <NotifyHost />
 *   </>);
 * `}</pre>
 *
 * <h3>설계 결정</h3>
 * <ul>
 *   <li>severity 별 제목 색상 — error 빨강 / warning 노랑 / success 초록 / info 회색.</li>
 *   <li>모서리 사각 (borderRadius: 0) — 페이지 전체 디자인 톤과 일관.</li>
 *   <li>{@code message} 에 \n 줄바꿈 그대로 반영 (DialogContentText whiteSpace: pre-line).</li>
 *   <li>{@code confirmAsync} 는 Promise 반환 — 호출 측 async/await 로 직관적 분기.</li>
 * </ul>
 */
import React, { useCallback, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
} from "@mui/material";
import ActionButton from "./ActionButton";

const severityColor = (severity) => {
    switch (severity) {
        case "error":   return "#dc2626";
        case "warning": return "#ca8a04";
        case "success": return "#16a34a";
        default:        return "#1e293b";
    }
};

/** 단독 사용 가능한 표시 컴포넌트. dialog={...}, onClose={(answer)=>{}} 형태로 제어. */
export function NotifyDialog({ dialog, onClose }) {
    return (
        <Dialog
            open={!!dialog}
            onClose={() => onClose && onClose(false)}
            PaperProps={{
                // 토스/슬랙 느낌의 살짝 둥근 카드 — 12px. 헤더 chip/버튼은 8px 로 한 단계 작게.
                sx: { borderRadius: "12px", minWidth: 360, maxWidth: 480, boxShadow: "0 24px 48px rgba(15,23,42,0.18)" },
            }}
        >
            {dialog && (
                <>
                    <DialogTitle sx={{
                        pb: 1, fontSize: 16, fontWeight: 800,
                        color: severityColor(dialog.severity),
                    }}>
                        {dialog.title || (dialog.mode === "confirm" ? "확인" : "알림")}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{
                            fontSize: 13.5,
                            color: "#334155",
                            whiteSpace: "pre-line",
                        }}>
                            {dialog.message}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ px: 2, pb: 1.5 }}>
                        {dialog.mode === "confirm" ? (
                            <>
                                <ActionButton variant="cancel" onClick={() => onClose && onClose(false)}
                                    label={dialog.cancelLabel || "취소"} />
                                <ActionButton variant="confirm" onClick={() => onClose && onClose(true)}
                                    autoFocus startIcon={false}
                                    label={dialog.confirmLabel || "확인"} />
                            </>
                        ) : (
                            <ActionButton variant="confirm" onClick={() => onClose && onClose(true)}
                                autoFocus startIcon={false}
                                label={dialog.confirmLabel || "확인"} />
                        )}
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

/**
 * 다이얼로그 상태 + helper 를 한 번에 제공하는 훅.
 * 반환값:
 *  - notify(opts)      — 알림(확인 버튼 1개)
 *  - confirmAsync(opts) → Promise<boolean>  — yes/no 확인
 *  - NotifyHost        — 컴포넌트 트리에 한 번 마운트할 host
 */
export function useNotifyDialog() {
    const [dialog, setDialog] = useState(null);

    const notify = useCallback((opts) => {
        setDialog({ mode: "notify", severity: "info", ...opts });
    }, []);

    const confirmAsync = useCallback((opts) => {
        return new Promise((resolve) => {
            setDialog({ mode: "confirm", severity: "warning", ...opts, _resolve: resolve });
        });
    }, []);

    const handleClose = useCallback((answer) => {
        // setDialog 가 비동기라 prev dialog 의 resolve 를 명확히 콜.
        setDialog((d) => {
            if (d?.mode === "confirm" && typeof d._resolve === "function") {
                d._resolve(answer === true);
            }
            return null;
        });
    }, []);

    const NotifyHost = useCallback(() => (
        <NotifyDialog dialog={dialog} onClose={handleClose} />
    ), [dialog, handleClose]);

    return { notify, confirmAsync, NotifyHost };
}
